import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Icon from '../components/common/Icon';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorBanner from '../components/common/ErrorBanner';
import { formatRelativeTime } from '../utils/formatters';
import { studiesAPI } from '../services/api';
import authService from '../services/authService';
import './AdminStudies.css';

const AdminStudies = () => {
    const isSuperAdmin = authService.isSuperAdmin();
    const [studies, setStudies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retrying, setRetrying] = useState(false);
    const [retryingAll, setRetryingAll] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const [retryingStudyId, setRetryingStudyId] = useState(null);

    useEffect(() => {
        fetchStudies();
    }, []);

    const fetchStudies = async () => {
        try {
            setLoading(true);
            const response = await studiesAPI.list({ skip: 0, take: 100 });
            const studiesArray = Array.isArray(response.items) ? response.items : [];
            
            // Filter only stuck studies (GoogleAdsInProgress or AuthoritasCompleted)
            const stuckStudies = studiesArray.filter(
                study => study.status === 'GoogleAdsInProgress' || 
                         study.status === 'AuthoritasCompleted'
            );
            
            setStudies(stuckStudies);
            setError(null);
        } catch (err) {
            setError(err.message || 'Erreur lors du chargement des études');
        } finally {
            setLoading(false);
        }
    };

    const handleRetryStudy = async (studyId) => {
        setRetrying(true);
        setRetryingStudyId(studyId);
        setError(null);
        setSuccessMessage(null);

        try {
            const response = await studiesAPI.retryGoogleAds(studyId);
            setSuccessMessage(`✓ Étude ${studyId.substring(0, 8)}... relancée avec succès (${response.volumesCount} volumes récupérés)`);
            
            // Refresh studies list
            await fetchStudies();
        } catch (err) {
            setError(err.message || 'Erreur lors de la relance de l\'étude');
        } finally {
            setRetrying(false);
            setRetryingStudyId(null);
        }
    };

    const handleRetryAll = async () => {
        if (!window.confirm(`Êtes-vous sûr de vouloir relancer toutes les ${studies.length} études bloquées ?`)) {
            return;
        }

        setRetryingAll(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const response = await studiesAPI.retryAllGoogleAds();
            setSuccessMessage(
                `✓ Traitement terminé : ${response.successCount} réussies, ${response.failedCount} échouées sur ${response.processedCount} études`
            );
            
            // Refresh studies list
            await fetchStudies();
        } catch (err) {
            setError(err.message || 'Erreur lors de la relance globale');
        } finally {
            setRetryingAll(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'GoogleAdsInProgress': { label: 'Google Ads en cours', color: 'orange' },
            'AuthoritasCompleted': { label: 'Authoritas terminé', color: 'blue' }
        };

        const statusInfo = statusMap[status] || { label: status, color: 'gray' };

        return (
            <span className={`status-badge status-${statusInfo.color}`}>
                {statusInfo.label}
            </span>
        );
    };

    if (loading) {
        return (
            <Layout>
                <div className="admin-studies-page">
                    <LoadingSpinner message="Chargement des études bloquées..." />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="admin-studies-page">
                {isSuperAdmin && (
                    <div className="superadmin-banner">
                        <div className="superadmin-badge">
                            <Icon name="Shield" size={18} />
                            <span>SuperAdmin</span>
                        </div>
                        <div className="superadmin-info">
                            Vous gérez les études bloquées de toutes les agences
                        </div>
                    </div>
                )}

                <div className="page-header">
                    <div>
                        <h1 className="page-title">
                            <Icon name="AlertTriangle" size={32} color="var(--color-warning)" />
                            Études bloquées
                        </h1>
                        <p className="page-subtitle">
                            Gérez les études bloquées en phase de calcul des volumes de recherche
                        </p>
                    </div>
                    
                    {studies.length > 0 && (
                        <Button
                            variant="primary"
                            onClick={handleRetryAll}
                            disabled={retryingAll}
                            icon={retryingAll ? null : <Icon name="RefreshCw" size={18} />}
                        >
                            {retryingAll ? 'Relance en cours...' : `Relancer toutes (${studies.length})`}
                        </Button>
                    )}
                </div>

                {error && (
                    <ErrorBanner 
                        message={error} 
                        onDismiss={() => setError(null)} 
                    />
                )}

                {successMessage && (
                    <div className="success-banner">
                        <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                        <span>{successMessage}</span>
                        <button onClick={() => setSuccessMessage(null)} className="dismiss-button">
                            <Icon name="X" size={16} />
                        </button>
                    </div>
                )}

                {studies.length === 0 ? (
                    <Card>
                        <div className="empty-state">
                            <Icon name="CheckCircle" size={64} color="var(--color-success)" />
                            <h3>Aucune étude bloquée</h3>
                            <p>Toutes les études sont dans un état normal.</p>
                            <Button 
                                variant="secondary" 
                                onClick={fetchStudies}
                                icon={<Icon name="RefreshCw" size={18} />}
                            >
                                Actualiser
                            </Button>
                        </div>
                    </Card>
                ) : (
                    <div className="studies-grid">
                        {studies.map((study) => (
                            <Card key={study.studyId} className="study-card">
                                <div className="study-header">
                                    <div className="study-info">
                                        <h3 className="study-domain">{study.clientDomain}</h3>
                                        <p className="study-id">ID: {study.studyId.substring(0, 8)}...</p>
                                    </div>
                                    {getStatusBadge(study.status)}
                                </div>

                                <div className="study-details">
                                    <div className="detail-row">
                                        <Icon name="Calendar" size={16} color="var(--color-gray-500)" />
                                        <span>Créée {formatRelativeTime(study.createdAt)}</span>
                                    </div>
                                    <div className="detail-row">
                                        <Icon name="Hash" size={16} color="var(--color-gray-500)" />
                                        <span>{study.totalKeywords || 0} mots-clés</span>
                                    </div>
                                    {study.errorMessage && (
                                        <div className="detail-row error-message">
                                            <Icon name="AlertCircle" size={16} color="var(--color-error)" />
                                            <span>{study.errorMessage}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="study-actions">
                                    <Button
                                        variant="primary"
                                        size="small"
                                        onClick={() => handleRetryStudy(study.studyId)}
                                        disabled={retrying && retryingStudyId === study.studyId}
                                        icon={
                                            retrying && retryingStudyId === study.studyId ? null : 
                                            <Icon name="RefreshCw" size={16} />
                                        }
                                    >
                                        {retrying && retryingStudyId === study.studyId 
                                            ? 'Relance...' 
                                            : 'Relancer Google Ads'
                                        }
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        size="small"
                                        onClick={() => window.location.href = `/projets/${study.studyId}`}
                                        icon={<Icon name="ExternalLink" size={16} />}
                                    >
                                        Voir détails
                                    </Button>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default AdminStudies;
